import { CSSProperties } from "react";
import styled, { DefaultTheme, css } from "styled-components";

import { TColor } from "../../theme";

interface ComponentProps {
  $isDisabled?: boolean;
  $hasError?: boolean;
  $color?: TColor | "currentColor" | "inherit";
  $type?: "mono" | "small" | "caption" | "topic";
  $size?: string;
  $isBold?: boolean;
  $fontWeight?: CSSProperties["fontWeight"];
  $decoration?: CSSProperties["textDecoration"];
  $lineHeight?: CSSProperties["lineHeight"];
  $alignText?: string;
  $noMargins?: boolean;
  $margin?: CSSProperties["margin"];
  $onHoverColor?: string;
  $maxLines?: number;
  $whiteSpace?: CSSProperties["whiteSpace"];
  $isUpperCase?: boolean;
  $isCapitalized?: boolean;
  $fontStyle?: CSSProperties["fontStyle"];
  $textWrap?: CSSProperties["textWrap"];
}

const baseCss = ({ $lineHeight }: ComponentProps) => css<ComponentProps>`
  line-height: ${$lineHeight ? $lineHeight : 1.5};
  margin: 0;
  ${({ $decoration }) =>
    $decoration &&
    css`
      text-decoration: ${$decoration};
    `};
`;

const getColor = ({
  $isDisabled,
  $hasError,
  theme,
  $color,
}: ComponentProps & { theme: DefaultTheme }) => {
  if ($hasError) {
    return theme.colors.red;
  }

  if ($isDisabled) {
    return theme.colors.disabledText;
  }

  if ($color === "currentColor" || $color === "inherit") {
    return $color;
  }

  return $color && $color in theme.colors
    ? theme.colors[$color]
    : theme.colors.primary;
};

const getHoverColor = ({
  $isDisabled,
  $hasError,
  theme,
  $onHoverColor,
}: ComponentProps & { theme: DefaultTheme }) => {
  if ($hasError) {
    return theme.colors.red;
  }

  if ($isDisabled) {
    return theme.colors.disabledText;
  }

  if ($onHoverColor === "currentColor" || $onHoverColor === "inherit") {
    return $onHoverColor;
  } else if ($onHoverColor === "secondary") {
    return "#76777a";
  }

  return $onHoverColor && $onHoverColor in theme.colors
    ? theme.colors[$onHoverColor]
    : theme.colors.primary;
};

const getFont = ({ $type }: ComponentProps) => {
  if ($type === "mono") {
    return css`
      font-size: 16px;
      font-family: monospace;
    `;
  } else if ($type === "small") {
    return css`
      font-size: 14px;
      @media only screen and (max-width: ${({ theme }) =>
          theme.media.breakpoint.smallX}px) {
        font-size: 12px;
      }
    `;
  } else if ($type === "caption") {
    return css`
      font-size: 12px;
      @media only screen and (max-width: ${({ theme }) =>
          theme.media.breakpoint.smallX}px) {
        font-size: 10px;
      }
    `;
  } else if ($type === "topic") {
    return css`
      font-size: 12px;
      letter-spacing: -0.03em;
      @media only screen and (max-width: ${({ theme }) =>
          theme.media.breakpoint.smallX}px) {
        font-size: 10px;
      }
    `;
  } else {
    return css`
      font-size: 16px;
    `;
  }
};

const addSize = (size?: string) => {
  if (!size) {
    return;
  }
  return css`
    font-size: ${size};
  `;
};

const maxLinesCss = (props: ComponentProps) => css<ComponentProps>`
  ${props.$maxLines
    ? css`
        overflow: hidden;
        display: -webkit-box;
        text-overflow: ellipsis;
        -webkit-line-clamp: ${props.$maxLines}; /* number of lines to show */
        -webkit-box-orient: vertical;
      `
    : ""}
`;
const changingCss = css<ComponentProps>`
  text-wrap: ${({ $textWrap }) => ($textWrap ? $textWrap : "wrap")};
  color: ${getColor};
  font-style: ${({ $fontStyle }) => ($fontStyle ? $fontStyle : "normal")};
  font-weight: ${({ $fontWeight, $isBold }) =>
    $fontWeight || ($isBold ? "bold" : "normal")};
  ${getFont};
  ${({ $size }) => addSize($size)};
  text-align: ${({ $alignText }) => ($alignText ? $alignText : "inherit")};
  ${({ $lineHeight }) =>
    $lineHeight &&
    css`
      line-height: ${$lineHeight};
    `};
  ${({ $whiteSpace }) => `white-space: ${$whiteSpace};`}
  ${({ $margin, $noMargins }) =>
    $noMargins
      ? css`
          margin: 0;
        `
      : $margin
        ? css`
            margin: ${$margin} !important;
          `
        : ""};
  ${({ $noMargins }) =>
    $noMargins &&
    css`
      margin: 0;
    `};
  ${maxLinesCss};
  ${({ $isUpperCase, $isCapitalized }) => {
    if ($isUpperCase) {
      return css`
        text-transform: uppercase;
      `;
    }

    if ($isCapitalized) {
      return css`
        text-transform: capitalize;
      `;
    }
  }}
  &:hover {
    color: ${getHoverColor};
  }
`;

export const InlineText = styled.span<ComponentProps>`
  display: inline-flex;
  align-items: center;
  ${baseCss};
  ${changingCss};
`;

export const Paragraph = styled.p<ComponentProps>`
  ${baseCss};
  ${changingCss};
`;

const baseHeaderCss = css`
  font-style: normal;
  font-family: ${({ theme }) => theme.fonts.DMSans};
`;

const changingHeaderCss = ({
  $alignText,
  $noMargins,
  $isBold = true,
  $fontWeight,
}: ComponentProps) => css<ComponentProps>`
  color: ${getColor};
  font-weight: ${$fontWeight || ($isBold ? "bold" : "normal")};
  text-align: ${$alignText ? $alignText : "inherit"};
  ${$noMargins ? "margin: 0;" : ""}
  ${({ $whiteSpace }) => `white-space: ${$whiteSpace};`}
  ${maxLinesCss};
`;

export const Heading1 = styled.h1<ComponentProps>`
  font-size: 40px;
  line-height: 1.2;
  letter-spacing: 0.02em;
  margin: 0 0 17px 0;
  @media only screen and (max-width: ${({ theme }) =>
      theme.media.breakpoint.smallX}px) {
    font-size: 28px;
  }
  ${baseHeaderCss}
  ${changingHeaderCss}
`;

export const Heading2 = styled.h2<ComponentProps>`
  color: ${getColor};
  font-size: 28px;
  line-height: 1.2;
  margin: 0 0 17px 0;
  letter-spacing: 0.02em;
  ${baseHeaderCss}
  ${changingHeaderCss}
`;

export const Heading3 = styled.h3<ComponentProps>`
  color: ${getColor};
  font-size: 24px;
  line-height: 1.5;
  margin: 0 0 16px 0;
  @media only screen and (max-width: ${({ theme }) =>
      theme.media.breakpoint.smallX}px) {
    font-size: 18px;
  }
  ${baseHeaderCss}
  ${(props) => changingHeaderCss({ $fontWeight: 400, ...props })}
`;

export const Heading4 = styled.h4<ComponentProps>`
  color: ${getColor};
  font-size: 18px;
  line-height: 1.48;
  margin: 0 0 6px 0;
  @media only screen and (max-width: ${({ theme }) =>
      theme.media.breakpoint.smallX}px) {
    font-size: 14px;
  }
  ${baseHeaderCss}
  ${changingHeaderCss}
`;

export const CTALink = styled.p<ComponentProps>`
  color: ${getColor};
  font-size: 16px;
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.06em;
  @media only screen and (max-width: ${({ theme }) =>
      theme.media.breakpoint.smallX}px) {
    font-size: 12px;
    letter-spacing: 0.06em;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  ${baseHeaderCss}
  ${changingHeaderCss}
`;
